import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import authService from '../services/AuthService';

const SignupPage = ({ isAuthenticated, onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [amazonMarketplace, setAmazonMarketplace] = useState('US');
  const navigate = useNavigate();

  const AMAZON_MARKETPLACES = [
    { code: 'US', domain: '.com', flag: '🇺🇸' },
    { code: 'UK', domain: '.co.uk', flag: '🇬🇧' },
    { code: 'CA', domain: '.ca', flag: '🇨🇦' },
    { code: 'MX', domain: '.com.mx', flag: '🇲🇽' },
  ];
  const location = useLocation();

  // If we were sent here mid-Walmart-connect, resume that flow first;
  // otherwise fall back to wherever the route guard sent us, or /dashboard.
  const getPendingWalmartRedirect = () => {
    try {
      const pending = sessionStorage.getItem('msp_pending_walmart');
      if (!pending) return null;
      const { walmartCallbackUri, clientType } = JSON.parse(pending);
      const qs = new URLSearchParams({ marketplace: 'walmart', walmartCallbackUri });
      if (clientType) qs.set('clientType', clientType);
      return `/msp-inventory?${qs.toString()}`;
    } catch {
      return null;
    }
  };

  const from = getPendingWalmartRedirect() || (location.state?.from
    ? location.state.from.pathname + (location.state.from.search || '') + (location.state.from.hash || '')
    : '/dashboard');

  useEffect(() => {
    if (isAuthenticated) {
      try { sessionStorage.removeItem('msp_pending_walmart'); } catch { /* ignore */ }
      navigate(from, { replace: true });
    }
  }, [isAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const result = await authService.register(email, password, (localStorage.getItem('affiliate_encoded') || ''), amazonMarketplace);

    if (result.success) {
      localStorage.removeItem('affiliate_encoded');
      onLogin(result.user);
    } else {
      setError(result.error || 'Registration failed');
    }
    setLoading(false);
  };

  return (<>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 -mt-16">
      {/* Animated background blobs */}
      <div className="fixed inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl" style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
        <div className="absolute top-0 -right-4 w-96 h-96 rounded-full filter blur-3xl" style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 rounded-full filter blur-3xl" style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
      </div>

      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md border-2 border-gray-200 relative z-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-teal-500 to-blue-600 bg-clip-text text-transparent">
            Create Account
          </h2>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
              minLength={8}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            />
          </div>

          {/* Amazon Marketplace */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amazon Marketplace <span className="text-gray-400 font-normal">(default: US)</span>
            </label>
            <div className="grid grid-cols-4 gap-2">
              {AMAZON_MARKETPLACES.map((mp) => (
                <button
                  key={mp.code}
                  type="button"
                  onClick={() => setAmazonMarketplace(mp.code)}
                  className={`py-2 px-1 border-2 rounded-lg flex flex-col items-center gap-0.5 transition ${amazonMarketplace === mp.code
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                  <span className={`text-xs font-semibold ${amazonMarketplace === mp.code ? 'text-teal-700' : 'text-gray-500'}`}>
                    {mp.code}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full text-white py-3 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50 shadow-lg"
            style={{
              background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)'
            }}
          >
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>

        <p className="text-center mt-6 text-gray-600">
          Already have an account?{' '}
          <Link to="/login" state={{ from: location.state?.from }} className="text-teal-600 font-semibold hover:text-teal-700">
            Login
          </Link>
        </p>
      </div>
    </div>
  </>);
};

export default SignupPage;